import { DateTimeFormatter, LocalDate } from '@js-joda/core';
import Highcharts from 'highcharts';
import { SpendingHistory, SpendingHistoryDataPoint } from 'src/types/spending';
import FormatService from 'src/services/format.service';
import LiteralsService from 'src/services/literals/LiteralsService';
import DateUtils from 'src/utils/date';

const { Locale } = require('@js-joda/locale_en-us');

const line1Color = '#006fcf';
const line1TopOpacity = 0.13;
const line1BottomOpacity = 0;
const line2Color = '#8E9092';
const targetSpendingLineColor = '#53565A';
const textColor = '#262626';
const axisColor = '#ECEDEE';
const lineWidth = 2;
const currentMonthSeriesName = LiteralsService.getLiteral('Budget.Current.graph.currentMonth');
const comparisonMonthSeriesName = LiteralsService.getLiteral('Budget.Current.graph.comparisonMonth');

const formatter = DateTimeFormatter.ofPattern('MMM').withLocale(Locale.ENGLISH);

export function getChartOptions(
    currentMonthSpendingHistory: SpendingHistory[],
    comparisonMonthSpendingHistory: SpendingHistory[],
    targetSpending?: number
): Highcharts.Options {
    const currentMonthData: SpendingHistoryDataPoint[] = currentMonthSpendingHistory?.[0]?.history || [];
    const comparisonMonthData: SpendingHistoryDataPoint[] = comparisonMonthSpendingHistory?.[0]?.history || [];
    const currentMonthStartDate: LocalDate = DateUtils.startDateOfThisMonth();
    const comparisonMonthStartDate: LocalDate = comparisonMonthData?.[0]?.timePeriodStart
        ? LocalDate.parse(comparisonMonthData?.[0]?.timePeriodStart)
        : DateUtils.startDateOfLastMonth(); // Default to the last month if no data
    const currentMonthLabel = currentMonthStartDate.format(formatter);
    const comparisonMonthLabel = comparisonMonthStartDate.format(formatter);

    const comparisonMonthDataFormatted = comparisonMonthData.map(({ spent }: any) => spent);
    const currentMonthDataFormatted = currentMonthData.map(({ spent }: any) => spent);
    const maxSpendingValue = Math.max(
        comparisonMonthDataFormatted?.slice(-1)[0] || 0,
        currentMonthDataFormatted?.slice(-1)[0] || 0,
        targetSpending || 0
    );

    /* Add 25% "padding" to
     maxSpendingValue and round up to nearest 100 */
    const maxChartValue = Math.max(Math.ceil((maxSpendingValue * 1.25) / 100), 1) * 100;
    /* Condensed means y-axis labels are written in 1000's with k suffix */
    const showCondensedLabelsOnMobile = maxChartValue > 10000;
    /* If showing condensed labels on mobile we want max value to be a multiple of 4000 */
    const maxChartValueMobile = showCondensedLabelsOnMobile ? 4000 * Math.ceil(maxChartValue / 4000) : maxChartValue;

    /* If there is no spending in the last month, set the max value of the y-axis to 100 */
    const defaultChartMax: number = maxSpendingValue ? undefined : 100;

    /* setup everything besides targetSpending
     * for some reason using type Highcharts.Options gives errors on properties that are documented */
    const chartOptions: any = {
        annotations: [],
        chart: {
            spacingLeft: 0,
            spacingRight: 0,
            marginRight: 5
        },
        legend: {
            enabled: false
        },
        plotOptions: {
            series: {
                marker: {
                    /* Disable markers for all series by default */
                    enabled: false,
                    symbol: 'circle'
                },
                states: {
                    hover: {
                        halo: {
                            /* Don't show halo around marker on hover */
                            size: 0
                        },
                        animation: {
                            /* No animation on hover */
                            duration: 0
                        }
                    },
                    inactive: {
                        /* don't fade out other charts on hover */
                        opacity: 1
                    },
                    normal: {
                        /* No animation from hover state to normal state */
                        animation: false
                    }
                }
            }
        },
        responsive: {
            rules: [
                {
                    condition: {
                        maxWidth: 500
                    },
                    chartOptions: {
                        yAxis: {
                            labels: {
                                distance: 10,
                                formatter: ({ value }: { value: number }) => {
                                    if (showCondensedLabelsOnMobile) {
                                        return `${FormatService.format(value / 1000)}k`;
                                    }
                                    return `${FormatService.format(value)}`;
                                }
                            },
                            tickInterval: maxChartValueMobile / 4,
                            softMax: maxChartValueMobile,
                            max: defaultChartMax
                        }
                    }
                }
            ]
        },
        series: [
            {
                accessibility: {
                    point: {
                        /* will get read at each point */
                        descriptionFormatter: function ({ x, y, series }: any) {
                            return `${comparisonMonthLabel} ${(x as number) + 1} ${FormatService.format(y)} spent. ${
                                series.name
                            }`;
                        }
                    }
                },
                color: line2Color,
                dashStyle: 'Dash',
                data: comparisonMonthDataFormatted.map((y, i) => {
                    return {
                        y,
                        /* format end marker */
                        marker:
                            i === comparisonMonthDataFormatted.length - 1
                                ? {
                                      enabled: true,
                                      /* outlined circle styles */
                                      fillColor: 'white',
                                      lineWidth,
                                      lineColor: line2Color
                                  }
                                : undefined
                    };
                }),
                legendSymbol: 'rectangle',
                lineWidth,
                marker: {
                    states: {
                        hover: {
                            lineColor: line2Color,
                            fillColor: 'white',
                            lineWidth,
                            radiusPlus: 0
                        }
                    }
                },
                name: comparisonMonthSeriesName,
                states: {
                    hover: {
                        lineWidthPlus: 0
                    }
                },
                type: 'line'
            },
            {
                accessibility: {
                    point: {
                        /* will get read at each point */
                        descriptionFormatter: function ({ x, y, series }: any) {
                            return `${currentMonthLabel} ${(x as number) + 1} ${FormatService.format(y)} spent. ${
                                series.name
                            }`;
                        }
                    }
                },
                color: line1Color,
                data: currentMonthDataFormatted.map((y, i) => {
                    return {
                        y,
                        /* format end marker */
                        marker:
                            i === currentMonthDataFormatted.length - 1
                                ? {
                                      enabled: true,
                                      /* pad the marker so it matches the outlined circle in last month's series */
                                      fillColor: line1Color,
                                      radius: 5,
                                      lineWidth,
                                      lineColor: 'white'
                                  }
                                : undefined
                    };
                }),
                fillColor: {
                    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                    stops: [
                        [0, Highcharts.color(line1Color).setOpacity(line1TopOpacity).get('rgba')],
                        [1, Highcharts.color(line1Color).setOpacity(line1BottomOpacity).get('rgba')]
                    ]
                },
                lineWidth,
                marker: {
                    states: {
                        hover: {
                            animation: {
                                duration: 0
                            },
                            fillColor: line1Color,
                            lineWidth,
                            radiusPlus: 0,
                            radius: 5
                        },
                        normal: {
                            animation: false
                        }
                    }
                },
                name: currentMonthSeriesName,
                states: {
                    hover: {
                        lineWidthPlus: 0
                    }
                },
                type: 'area'
            }
        ],
        title: {
            text: undefined
        },
        tooltip: {
            backgroundColor: '#F8F9FA',
            borderColor: '#C8C9C7',
            borderRadius: 1,
            borderWidth: 1,
            formatter: function (this: any): string {
                return `<span style="font-size: 12px; font-weight: 500; color: ${textColor}; line-height: 18px">
                    <span>${
                        this.series?.name === comparisonMonthSeriesName ? comparisonMonthLabel : currentMonthLabel
                    } ${(this.x as number) + 1}, ${
                        this.series?.name === comparisonMonthSeriesName
                            ? comparisonMonthStartDate.year()
                            : currentMonthStartDate.year()
                    }</span>
                    <br/>
                    <span>${FormatService.formatCurrency(this.y)}</span>
                </span>`;
            },
            shadow: false,
            useHTML: true
        },
        xAxis: {
            accessibility: {
                description: 'date'
            },
            min: 0,
            max: 31,
            categories: [0, 1, 2, 3],
            gridLineWidth: 0,
            labels: {
                formatter: ({ value }: { value: number }) => `${currentMonthLabel} ${value + 1}`,
                style: {
                    color: textColor,
                    fontSize: '15px'
                }
            },
            lineWidth: 0,
            /* remove padding between chart edge and line */
            maxPadding: 0,
            minPadding: 0,
            /* Add individual plot lines instead of on ticks */
            plotLines: currentMonthData.length
                ? [
                      {
                          color: axisColor,
                          width: 1,
                          value: 6
                      },
                      {
                          color: axisColor,
                          width: 1,
                          value: 13
                      },
                      {
                          color: axisColor,
                          width: 1,
                          value: 20
                      },
                      {
                          color: axisColor,
                          width: 1,
                          value: 27
                      }
                  ]
                : [],
            tickPositions: [0, 6, 13, 20, 27],
            tickWidth: 0
        },
        yAxis: {
            accessibility: {
                description: 'dollars spent'
            },
            gridLineColor: axisColor,
            labels: {
                formatter: ({ value }: { value: number }) => `${FormatService.format(value)}`,
                style: {
                    color: textColor,
                    fontSize: '15px'
                }
            },
            max: defaultChartMax,
            softMax: maxChartValue,
            lineWidth: 0,
            tickInterval: maxChartValue / 4,
            title: {
                text: undefined
            }
        },
        lang: {
            noData: LiteralsService.getLiteral('Budget.Current.chartNoData')
        },
        noData: {
            style: {
                fontSize: '14px',
                fontWeight: 400,
                color: '#53565A',
                lineHeight: '20px'
            },
            position: {
                y: -45
            }
        }
    };

    /* setup targetSpending */
    if (targetSpending) {
        chartOptions.series.push({
            color: targetSpendingLineColor,
            data: Array(Math.max(comparisonMonthData.length, currentMonthData.length)).fill(targetSpending),
            /* do data calculation within dict, so it's not calculated when not rendered */
            enableMouseTracking: false,
            lineWidth,
            marker: {
                enabled: false
            },
            name: 'Target Spending',
            type: 'line'
        });
        chartOptions.annotations.push({
            draggable: '',
            labels: [
                {
                    align: 'left',
                    backgroundColor: targetSpendingLineColor,
                    borderRadius: 15,
                    borderWidth: 0,
                    padding: 8,
                    point: { xAxis: 0, yAxis: 0, x: 2, y: targetSpending },
                    style: {
                        fontSize: '12px',
                        fontWeight: '700'
                    },
                    text: `${FormatService.format(targetSpending)} target`,
                    y: 15
                }
            ]
        });
    }

    return chartOptions;
}
